import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { SigningTokenInfo } from '@corner/shared';
import { recipientViewFields } from '../../lib/signingRecipientView';

const API_BASE = import.meta.env.VITE_API_URL || '';

export default function SigningEntryPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [info, setInfo] = useState<SigningTokenInfo | null>(null);
  const [error, setError] = useState<{ type: 'expired' | 'not_found' | 'revoked'; message: string; senderName?: string; senderEmail?: string; expiresAt?: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verificationError, setVerificationError] = useState('');

  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE}/api/signing/token/${token}`)
      .then(async (res) => {
        if (res.ok) return res.json();
        const data = await res.json().catch(() => ({}));
        const code = data.code as string | undefined;
        if (code === 'TOKEN_EXPIRED') {
          setError({ type: 'expired', message: 'This signing link has expired.', senderName: data.senderName, senderEmail: data.senderEmail, expiresAt: data.expiresAt });
          return null;
        }
        if (code === 'TOKEN_REVOKED') {
          setError({ type: 'revoked', message: 'This signing request has been revoked.', senderName: data.senderName, senderEmail: data.senderEmail });
          return null;
        }
        setError({ type: 'not_found', message: 'Signing link not found.' });
        return null;
      })
      .then((data: SigningTokenInfo | null) => {
        if (!data) return;
        setInfo(data);
        if (data.signerStatus === 'signed') {
          navigate(`/sign/${token}/complete`, { replace: true });
        }
      })
      .catch(() => {
        setError({ type: 'not_found', message: 'Network error or invalid link.' });
      })
      .finally(() => setLoading(false));
  }, [token, navigate]);

  useEffect(() => {
    if (!loading && error) {
      const params = new URLSearchParams({ type: error.type });
      if (error.senderName) params.set('sender', error.senderName);
      if (error.senderEmail) params.set('email', error.senderEmail);
      if (error.expiresAt) params.set('expiry', error.expiresAt);
      navigate(`/sign/${token}/error?${params.toString()}`, { replace: true });
    }
  }, [loading, error, token, navigate]);

  const handleContinue = async () => {
    if (!info || !token) return;

    if (info.requireEmailVerification) {
      setVerifying(true);
      setVerificationError('');
      try {
        const res = await fetch(`${API_BASE}/api/signing/token/${token}/verify-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: verificationEmail.trim() }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          setVerificationError(typeof data.error === 'string' ? data.error : 'Email verification failed');
          return;
        }
      } catch {
        setVerificationError('Network error. Please try again.');
        return;
      } finally {
        setVerifying(false);
      }
    }

    fetch(`${API_BASE}/api/signing/token/${token}/view`, { method: 'POST' }).catch(() => {});
    navigate(`/sign/${token}/workspace`);
  };

  if (loading) {
    return (
      <div style={loadingStyle}>
        <p style={{ margin: 0, fontSize: 14, color: '#9E9189', fontFamily: 'Geist, system-ui, sans-serif' }}>Loading...</p>
      </div>
    );
  }

  if (error || !info) return null;

  const senderInitials = info.senderName.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2);
  const yourFields = recipientViewFields(info.fields, info.pageCount);
  const maskedEmail = info.signerEmail
    ? info.signerEmail.replace(/^(.)(.*)(@.*)$/, (_, first, middle, domain) => first + '*'.repeat(Math.min(middle.length, 3)) + domain)
    : '';
  const expiresDate = info.expiresAt
    ? new Date(info.expiresAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : null;

  return (
    <div style={pageStyle}>
      {/* Top Bar */}
      <div style={topBarStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, flexShrink: 0 }}>
            <svg width="24" height="24" viewBox="0 0 256 256" fill="none" style={{ flexShrink: 0 }}>
              <defs>
                <linearGradient id="cornerLogoEntry" x1="42" y1="38.5" x2="207" y2="209" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#D46A3A" /><stop offset="1" stopColor="#8B3A10" />
                </linearGradient>
              </defs>
              <path d="M236 152V32C236 25.373 230.627 20 224 20H32C25.373 20 20 25.373 20 32L20 224C20 230.627 25.373 236 32 236H152" stroke="url(#cornerLogoEntry)" strokeWidth="40" strokeLinecap="round" />
            </svg>
          </div>
          <span style={{ fontSize: 14, fontWeight: 600, color: '#3D3229', lineHeight: '18px', fontFamily: 'Geist, system-ui, sans-serif' }}>Corner</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#B5AA9C" strokeWidth="2" style={{ flexShrink: 0 }}>
            <rect x="3" y="11" width="18" height="11" rx="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          <span style={{ fontSize: 12, fontWeight: 400, color: '#B5AA9C', lineHeight: '16px', fontFamily: 'Geist, system-ui, sans-serif' }}>Secure connection</span>
        </div>
      </div>

      {/* Centered Card */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div style={cardStyle}>
          {/* Hero Section */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
            {/* Pen icon */}
            <div style={penIconStyle}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#D97645" strokeWidth="1.5" strokeLinecap="round" style={{ flexShrink: 0 }}>
                <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
              </svg>
            </div>
            {/* Title + subtitle */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
              <h1 style={titleStyle}>Document signing request</h1>
              <p style={subtitleStyle}>
                You'll be guided through each step. No account required.
              </p>
            </div>
          </div>

          {/* Sender Info */}
          <div style={senderCardStyle}>
            <div style={avatarStyle}>{senderInitials}</div>
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, gap: 1, minWidth: 0 }}>
              <span style={{ fontSize: 14, fontWeight: 500, color: '#3D3229', lineHeight: '18px', fontFamily: 'Geist, system-ui, sans-serif' }}>
                {info.senderName}
              </span>
              {info.senderEmail && (
                <span style={{ fontSize: 12, fontWeight: 400, color: '#9E9189', lineHeight: '16px', fontFamily: 'Geist, system-ui, sans-serif' }}>
                  {info.senderEmail}
                </span>
              )}
            </div>
            <span style={senderLabelStyle}>Sender</span>
          </div>

          {/* Document Details */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Filename row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#D97645" strokeWidth="1.5" style={{ flexShrink: 0 }}>
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
                <path d="M14 2v6h6" />
              </svg>
              <span style={{ fontSize: 15, fontWeight: 500, color: '#3D3229', lineHeight: '18px', fontFamily: 'Geist, system-ui, sans-serif' }}>
                {info.documentName}
              </span>
            </div>
            {/* 3-column metadata */}
            <div style={{ display: 'flex', gap: 0 }}>
              <div style={metaColumnStyle}>
                <span style={metaLabelStyle}>Your role</span>
                <span style={metaValueStyle}>{info.signerRole}</span>
              </div>
              <div style={metaColumnStyle}>
                <span style={metaLabelStyle}>To sign</span>
                <span style={metaValueStyle}>{yourFields.length} field{yourFields.length !== 1 ? 's' : ''}</span>
              </div>
              <div style={metaColumnStyle}>
                <span style={metaLabelStyle}>Pages</span>
                <span style={metaValueStyle}>{info.pageCount}</span>
              </div>
            </div>
          </div>

          {/* Verification + CTA */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Verification input */}
            {info.requireEmailVerification && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 500, color: '#3D3229', lineHeight: '16px', fontFamily: 'Geist, system-ui, sans-serif' }}>
                  Verify your email to continue
                </span>
                <input
                  type="email"
                  value={verificationEmail}
                  onChange={(e) => { setVerificationEmail(e.target.value); setVerificationError(''); }}
                  placeholder="Enter the invited email address"
                  style={inputStyle}
                />
                <span style={{ fontSize: 11, fontWeight: 400, color: '#B5AA9C', lineHeight: '14px', fontFamily: 'Geist, system-ui, sans-serif' }}>
                  Enter the email address that received this signing link: {maskedEmail}
                </span>
                {verificationError && (
                  <span style={{ fontSize: 11, fontWeight: 400, color: '#C62828', lineHeight: '14px', fontFamily: 'Geist, system-ui, sans-serif' }}>
                    {verificationError}
                  </span>
                )}
              </div>
            )}
            {/* CTA Button */}
            <button
              onClick={handleContinue}
              disabled={verifying || (info.requireEmailVerification && !verificationEmail.trim())}
              style={{
                ...ctaButtonStyle,
                cursor: verifying ? 'default' : 'pointer',
                opacity: (info.requireEmailVerification && !verificationEmail.trim()) ? 0.5 : 1,
              }}
            >
              {verifying ? 'Verifying...' : 'View document'}
            </button>
          </div>

          {/* Footer Badges */}
          <div style={footerBadgesStyle}>
            <div style={badgeStyle}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#B5AA9C" strokeWidth="2" style={{ flexShrink: 0 }}>
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              <span style={badgeTextStyle}>Encrypted</span>
            </div>
            <div style={badgeStyle}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#B5AA9C" strokeWidth="2" style={{ flexShrink: 0 }}>
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
                <path d="M14 2v6h6" />
                <path d="M9 15l2 2 4-4" />
              </svg>
              <span style={badgeTextStyle}>Audit trail</span>
            </div>
            {expiresDate && (
              <div style={badgeStyle}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#B5AA9C" strokeWidth="2" style={{ flexShrink: 0 }}>
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 6v6l4 2" />
                </svg>
                <span style={badgeTextStyle}>Expires {expiresDate}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Shared Styles ──────────────────────────────────────

const loadingStyle: React.CSSProperties = {
  minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
  backgroundColor: '#F5F2ED', fontFamily: 'Geist, system-ui, sans-serif',
};

const pageStyle: React.CSSProperties = {
  minHeight: '100vh', display: 'flex', flexDirection: 'column',
  backgroundColor: '#F5F2ED', fontFamily: 'Geist, system-ui, sans-serif',
};

const topBarStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  paddingBlock: 16, paddingInline: 32, flexShrink: 0,
};

const cardStyle: React.CSSProperties = {
  width: 480, backgroundColor: '#FFFFFF',
  borderRadius: 16,
  paddingTop: 48, paddingRight: 44, paddingBottom: 40, paddingLeft: 44,
  boxShadow: '0px 4px 24px #0000000F',
  display: 'flex', flexDirection: 'column', gap: 32,
};

const penIconStyle: React.CSSProperties = {
  width: 56, height: 56, borderRadius: 14,
  backgroundColor: '#FDF5EF',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  flexShrink: 0,
};

const titleStyle: React.CSSProperties = {
  margin: 0, fontSize: 26, fontWeight: 400, color: '#3D3229',
  fontFamily: '"Instrument Serif", system-ui, sans-serif',
  lineHeight: '32px', textAlign: 'center',
};

const subtitleStyle: React.CSSProperties = {
  margin: 0, fontSize: 14, fontWeight: 400, color: '#6B5E52',
  lineHeight: '21px', textAlign: 'center',
  fontFamily: 'Geist, system-ui, sans-serif',
};

const senderCardStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 12,
  borderRadius: 10, paddingBlock: 14, paddingInline: 16,
  backgroundColor: '#FAFAF8',
  borderWidth: 1, borderStyle: 'solid', borderColor: '#E8DED4',
};

const avatarStyle: React.CSSProperties = {
  width: 36, height: 36, borderRadius: 18,
  backgroundColor: '#D97645',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  fontSize: 14, fontWeight: 600, color: '#FFFFFF', flexShrink: 0,
  lineHeight: '18px', fontFamily: 'Geist, system-ui, sans-serif',
};

const senderLabelStyle: React.CSSProperties = {
  fontSize: 11, fontWeight: 400, textTransform: 'uppercase',
  letterSpacing: '0.04em', color: '#9E9189', lineHeight: '14px',
  fontFamily: 'Geist, system-ui, sans-serif', flexShrink: 0,
};

const metaColumnStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 4,
  flex: 1,
};

const metaLabelStyle: React.CSSProperties = {
  fontSize: 11, fontWeight: 400, textTransform: 'uppercase',
  letterSpacing: '0.04em', color: '#9E9189', lineHeight: '14px',
  fontFamily: 'Geist, system-ui, sans-serif',
};

const metaValueStyle: React.CSSProperties = {
  fontSize: 13, fontWeight: 500, color: '#3D3229', lineHeight: '16px',
  fontFamily: 'Geist, system-ui, sans-serif',
};

const inputStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box',
  border: '1px solid #E8DED4', borderRadius: 8,
  paddingBlock: 10, paddingInline: 14,
  fontSize: 13, fontFamily: 'Geist, system-ui, sans-serif',
  color: '#3D3229', backgroundColor: '#FAFAF8',
  outline: 'none', lineHeight: '16px',
};

const ctaButtonStyle: React.CSSProperties = {
  width: '100%', border: 'none', borderRadius: 8,
  paddingBlock: 12, paddingInline: 32,
  backgroundColor: '#2E2318', color: '#FDFCFB',
  fontSize: 14, fontWeight: 500, lineHeight: '18px',
  fontFamily: 'Geist, system-ui, sans-serif',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};

const footerBadgesStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  paddingTop: 4, gap: 16,
};

const badgeStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 4,
};

const badgeTextStyle: React.CSSProperties = {
  fontSize: 11, fontWeight: 400, color: '#B5AA9C', lineHeight: '14px',
  fontFamily: 'Geist, system-ui, sans-serif',
};
